/**
 * Trustees Page Component
 * Displays the profiles, roles, bios, and contact details of the Clipstone Miners Welfare Board of Trustees.
 * Fetches updated trustee records from the backend with static fallback support.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchJsonWithBackendFallback } from '../../utils/backendApi';
import './Trustees.css';

// Default static fallback trustee profiles
const defaultStaffMembers = [
  { id: 'paul-griffiths', name: 'Paul Griffiths', role: 'Trustee', phone: '07882 267686', email: 'paul@wildnights.co.uk', bio: 'Chair of the Trustee Board, focused on community outreach and strategic planning.' },
  { id: 'anne-griffiths', name: 'Anne Griffiths', role: 'Trustee', phone: '07889 046460', email: 'anne@wildnights.co.uk', bio: 'Leads local engagement programs and supports youth activities through the trust.' },
  { id: 'barbara-brownley', name: 'Barbara Brownley', role: 'Trustee', phone: '07730 794936', email: 'barbarabrownley@virginmedia.com', bio: 'Responsible for membership communications and volunteer coordination.' },
  { id: 'john-vardy', name: 'John Vardy', role: 'Trustee', phone: '07496 912219', email: 'vardyj84@hotmail.co.uk', bio: 'Oversees club activities and works closely with sports groups in the community.' },
  { id: 'ian-woodall', name: 'Ian Woodall', role: 'Trustee', phone: '07474 222232', email: 'iwoodall@hotmail.co.uk', bio: 'Supports facility development and maintains the trust’s collaboration with local partners.' },
  { id: 'richard-clarey', name: 'Richard Clarey', role: 'Trustee', phone: '07850 051986', email: 'rcsrichard@yahoo.co.uk', bio: 'Focuses on charity relationships and the trust’s long-term sustainability plans.' },
];

const normalizeTrusteeValue = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const Trustees = () => {
  const [staffMembers, setStaffMembers] = useState(defaultStaffMembers);
  const [contentItems, setContentItems] = useState([]);
  const [hoveredTrusteeId, setHoveredTrusteeId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedTrustees = window.localStorage.getItem('clipstone_trustees');
    if (storedTrustees) {
      try {
        const parsedTrustees = JSON.parse(storedTrustees);
        if (Array.isArray(parsedTrustees) && parsedTrustees.length > 0) {
          setStaffMembers(parsedTrustees);
        }
      } catch (err) {
        console.error('Unable to load trustees for public page:', err);
      }
    }
  }, []);

  useEffect(() => {
    const loadContentItems = async () => {
      try {
        const data = await fetchJsonWithBackendFallback('/bookings/category-content');
        const nextItems = Array.isArray(data) ? data : [];
        setContentItems(nextItems);
        window.localStorage.setItem('clipstone_category_content', JSON.stringify(nextItems));
      } catch (err) {
        console.error('Unable to load trustee activity associations:', err);
        const storedContent = window.localStorage.getItem('clipstone_category_content');
        if (storedContent) {
          try {
            const parsedContent = JSON.parse(storedContent);
            if (Array.isArray(parsedContent)) {
              setContentItems(parsedContent);
            }
          } catch (parseErr) {
            console.error('Unable to parse stored trustee activity associations:', parseErr);
          }
        }
      }
    };

    loadContentItems();
  }, []);

  return (
    <div className="trustees-page">
      <h1 className="trustees-title">Meet Our Trustees</h1>
      <p className="trustees-subtitle">Dedicated to supporting our community and preserving our heritage.</p>

      <div className="trustees-grid">
        {staffMembers.map((member, index) => {
          const initials = member.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase();

          const associatedActivities = contentItems.filter((item) => {
            const candidateValues = [item.trustee_id, item.trustee, item.trustee_name, item.assigned_trustee_name, item.associated_trustee_name].filter(Boolean);
            const memberVariants = [member.id, member.name, member.name?.toLowerCase().replace(/\s+/g, '-')].filter(Boolean).map(normalizeTrusteeValue);
            return candidateValues.some((value) => memberVariants.includes(normalizeTrusteeValue(value)));
          });
          const isActive = hoveredTrusteeId === member.id;
          const firstAssociatedActivity = associatedActivities[0];
          return (
            <div
              key={member.id}
              className={`profile-card ${isActive ? 'active' : ''}`}
              tabIndex="0"
              onMouseEnter={() => setHoveredTrusteeId(member.id)}
              onMouseLeave={() => setHoveredTrusteeId(null)}
              onFocus={() => setHoveredTrusteeId(member.id)}
              onBlur={() => setHoveredTrusteeId(null)}
              onClick={() => {
                if (firstAssociatedActivity) {
                  navigate(`/trust/${firstAssociatedActivity.category || 'community'}?trustee=${member.id}`);
                }
              }}
            >
              <div className="profile-image-container">
                <div className="profile-image">
                  {initials}
                </div>
              </div>

              <div className="profile-info">
                <h3 className="profile-name">{member.name}</h3>
                <div className="profile-role">{member.role}</div>

                <div className="profile-contact">
                  {member.phone && (
                    <a href={`tel:${member.phone.replace(/\s+/g, '')}`} className="contact-item" onClick={(e) => e.stopPropagation()}>
                      <svg className="contact-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                      {member.phone}
                    </a>
                  )}
                  {member.email && (
                    <a href={`mailto:${member.email}`} className="contact-item" onClick={(e) => e.stopPropagation()}>
                      <svg className="contact-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                      {member.email}
                    </a>
                  )}
                </div>

                <div className={`profile-card-overlay ${isActive ? 'active' : ''}`}>
                  <div className="profile-card-overlay-inner">
                    <h3>{member.name}</h3>
                    <p className="profile-card-overlay-role">{member.role}</p>
                    <p className="profile-card-overlay-bio">{member.bio || 'No additional trustee profile details are available.'}</p>
                    <div className="profile-card-overlay-list">
                      <div><strong>Phone:</strong> {member.phone || 'Not provided'}</div>
                      <div><strong>Email:</strong> {member.email || 'Not provided'}</div>
                    </div>
                    <div className="profile-card-overlay-activities">
                      <h4>Associated activity information</h4>
                      {associatedActivities.length > 0 ? (
                        <>
                          <p className="profile-card-click-hint">Click the card to view the associated activity page.</p>
                          <ul>
                            {associatedActivities.map((activity) => (
                              <li key={activity.id}>{activity.title || 'Untitled activity'}</li>
                            ))}
                          </ul>
                        </>
                      ) : (
                        <p>No activity information is linked to this trustee yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Trustees;